export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
  ) {
    const friendlyMessages: Record<string, string> = {
      device_offline: 'Your phone is offline. Connect it to the internet and try again.',
      send_rate_limit_reached: 'Too many messages were queued recently. Wait a moment and try again.',
      password_reset_invalid_or_expired: 'This reset link is invalid or has expired. Request a new one.',
      new_password_must_be_different: 'Choose a password different from your current password.',
      subscription_unavailable: 'That SIM is no longer available on this phone. Refresh its SIM details and choose again.',
      sim_change_acknowledgement_required: 'Review and acknowledge the SIM change before enabling relay sends.',
      pairing_expired: 'This connection code expired. Create a new one and scan it again.',
      installation_already_pro: 'This phone already has Pro. Refresh its status, then use Manage with Stripe.',
      installation_subscription_exists: 'This phone already has a subscription. Refresh its status, then manage its payment with Stripe.',
      billing_not_configured: 'Billing is not available yet. The free allowance remains available.',
    };
    super(message ?? friendlyMessages[code] ?? code.replaceAll('_', ' '));
  }
}

function csrfToken(): string | null {
  return sessionStorage.getItem('messavo_csrf');
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);
  if (options.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const csrf = csrfToken();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && csrf) headers.set('x-csrf-token', csrf);
  const response = await fetch(path, { ...options, headers, credentials: 'include' });
  if (response.status === 204) return undefined as T;
  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    const message = typeof payload.message === 'string' && payload.message.trim() ? payload.message : undefined;
    throw new ApiError(response.status, String(payload.error ?? 'request_failed'), message);
  }
  return payload as T;
}

export async function refreshCsrf(): Promise<string> {
  const result = await api<{ csrfToken: string }>('/api/auth/csrf');
  sessionStorage.setItem('messavo_csrf', result.csrfToken);
  return result.csrfToken;
}

export function saveCsrf(token?: string): void {
  if (token) sessionStorage.setItem('messavo_csrf', token);
}

export function clearCsrf(): void {
  sessionStorage.removeItem('messavo_csrf');
}
